import React from 'react'

import type { PresentationBlockProps } from '../../'
import compareBlockProps from '../../util/compareBlockProps'

import { Grid, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'

import { socialLinkTypesByLabel } from './socialLinkTypes'
import { sanitizeHref } from './sanitizeHref'

const useStyles = makeStyles<Theme, PresentationBlockProps>(
  (_theme: Theme) => {
    return {
      root: {},
      // `color` is set on the icon, not on its `<a>`: getPresentationContentStylesNext styles
      // `& a` with the theme accent color, and that rule (a descendant selector) beats a class on
      // the anchor itself. The svg is a different element, so its own class wins over what it
      // would otherwise inherit from the anchor. An unset color keeps that inherited accent.
      linkIcon: (props) => ({
        fontSize: props.block.fields?.fontSize,
        color: props.block.fields?.color || undefined,
        '&:hover': {
          opacity: 0.8
        }
      })
    }
  },
  { name: 'SocialLinksBlock' }
)

const getIcon = (label: string, className: string) => {
  const Icon = socialLinkTypesByLabel[label]?.Icon
  return Icon ? <Icon className={className} /> : null
}

const getHref = (link: { label: string; href: string }) => {
  const { prefix = '' } = socialLinkTypesByLabel[link.label] ?? {}
  return sanitizeHref(`${prefix}${link.href}`)
}

function SocialLinksBlock(props: PresentationBlockProps) {
  const { block, WrapperComponent, isEditable, isSettings } = props
  const classes = useStyles(props)
  const { links, alignment, color } = block.fields
  // `.themeAccent` is `.content .themeAccent` — more specific than the icon's own class — so it
  // has to come off once the block carries a color of its own.
  const iconClassName = color ? classes.linkIcon : `${classes.linkIcon} themeAccent`

  const activeLinks = (links ?? []).filter((link) => {
    return link.href.length
  })

  return (
    <WrapperComponent {...props}>
      <Grid container spacing={2} justifyContent={alignment}>
        {(isEditable || !isSettings) && (
          <>
            {activeLinks.map((link) => {
              return (
                <Grid item key={link.label}>
                  <a href={getHref(link)} target="_blank" rel="noopener noreferrer">
                    {getIcon(link.label, classes.linkIcon)}
                  </a>
                </Grid>
              )
            })}
          </>
        )}
        {!isEditable && isSettings && (
          <>
            {activeLinks.map((link) => {
              return (
                <Grid item key={link.label}>
                  <span>{getIcon(link.label, iconClassName)}</span>
                </Grid>
              )
            })}
          </>
        )}
      </Grid>
    </WrapperComponent>
  )
}

export default React.memo(SocialLinksBlock, (prevProps, nextProps) => {
  return compareBlockProps(prevProps, nextProps)
})
